import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTos";
import {
    getCurrentShipTosDataView,
    getCurrentShipToState,
} from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BillToModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import BillToSelector, { BillToSelectorStyles } from "@insite/content-library/Components/BillToSelector";
import CustomerAddressForm, { CustomerAddressFormStyles } from "@insite/content-library/Components/CustomerAddressForm";
import BillingAddressForm, {
    BillingAddressFormStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/BillingAddressForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import EditIcon from "@MasterBluePrint/Icons/EditIcon";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React, { useEffect, useState } from "react";
import { connect,ResolveThunks  } from "react-redux";
import { css } from "styled-components";


interface OwnProps {
    address: BillToModel;
    onChange: (address: BillToModel) => void;
    countries: CountryModel[];
    addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    extendedStyles?: BillingAddressStyles;
}
const mapDispatchToProps = {
    loadCurrentShipTos,
};
const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    return {
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isBillingAddressUpdateRequired: state.pages.checkoutShipping.isBillingAddressUpdateRequired,
        cart: cartState.value,
        currentShipTosDataView: getCurrentShipTosDataView(state),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasToasterContext& ResolveThunks<typeof mapDispatchToProps>;

export interface BillingAddressStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    selectSavedAddressLink?: LinkPresentationProps;
    addressBookModal?: ModalPresentationProps;
    billToSelector?: BillToSelectorStyles;
    addressBookModalButtonsWrapper?: InjectableCss;
    addressBookModalCancelButton?: ButtonPresentationProps;
    addressDisplayAndFormGridItem?: GridItemProps;
    addressForm?: BillingAddressFormStyles;
    customAddressBookModal?: InjectableCss;
    addressDisplay?: AddressInfoDisplayStyles;
    editAddressForm?: CustomerAddressFormStyles;
    closeContainer?: InjectableCss;
}

export const billingAddressStyles: BillingAddressStyles = {
    container: { 
        gap: 20,
        css: css`
            div[class^='GridOffset'],
            div[class*=' GridOffset'] {
                width: 100%;
            }
            address {
                color: ${themeColors.colorAddress};
                font-family: 'Lato', sans-serif;
                font-size: ${themeFontSizes.size16};
                letter-spacing: 0.0313rem;
                line-height:  ${themeFontSizes.size24};

                p, span {
                    color: ${themeColors.colorAddress};
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "min",
                )
            };

            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [   
                    null,
                    null,
                    css`
                        div[class^='GridOffset'],
                        div[class*=' GridOffset'] {
                            width: calc(100% + 20px);
                        }
                    `,
                    null,
                ],
                "max",
            )};
        `,
    },
    closeContainer: {
        css: css`
           position: absolute;
           left: 1.8rem;
           top: 1.5rem;
           cursor: pointer;
           z-index: 9;

           .close-label {
                color: ${themeColors.colorMidGrey}
                font-family: 'Roboto', sans-serif;
                font-size: ${themeFontSizes.size14};
                letter-spacing: 0;
                line-height: 1;
                display: inline-block;
                margin-right: .525rem;
                vertical-align: middle;
           }
           .icon-close {
                display: inline-block;
                cursor: pointer;
                vertical-align: middle;
                line-height: 1;
                width: 1rem;
                height: 1rem;
                svg {
                    height: 1rem;
                    width: 1rem;
                    color: ${themeColors.colorMidGrey}
                }
            }
            @media screen and (max-width: 990px) {
                right: 1.25rem;
            }
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
            justify-content: space-between;

            h2.section-heading,
            h3.section-heading {
                color: ${themeColors.colorPrimary};
                font-family: 'Roboto', sans-serif;
                font-size: ${themeFontSizes.size20};
                font-weight: ${themeFontWeights.fontWeightForBold};
                letter-spacing: 0;
                line-height: 1.375rem;
                margin-bottom: 1.25rem;
            }
            .mb-present{
                margin-bottom: .438rem;
            }
        `,        
    },
    headingText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
            color: ${themeColors.colorHeadText};
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size18};
            font-weight: ${themeFontWeights.fontWeightMedium};
            letter-spacing: 0;
            line-height: 1.4375rem;
        `,
    },
    selectSavedAddressLink: {
        css: css`
            margin-left: 1.25rem;
            padding-left: 0.9375rem;
            margin-right: 1.875rem;
            border-left: 0.0625rem solid ${themeColors.colorBorderNew};

            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [   
                    null,
                    css`
                        margin-right: 0;
                    `,
                    null,
                    null,
                ],
                "max",
            )};
        `,
    },
    addressBookModal: { sizeVariant: "medium" },
    addressBookModalButtonsWrapper: {
        css: css`
        position: relative;
            display: flex;
            flex-direction: column;
            margin-top: 2rem;
            margin-bottom: 0;
            justify-content: center;
            align-items: center;
        `,
    },
    customAddressBookModal: {
        css: css`
            &.edit-billing-modal {
                div[class^='ModalContent'], div[class*=' ModalContent']{
                    position: relative;
                    padding-bottom: 6.9375rem;
                    height: calc(100vh - 6.3125rem);

                    @media screen and (max-width: 767px) {
                        padding-bottom: 6.9375rem;
                        height: calc(100vh - 6.3125rem);
                    }
                    @media screen and (max-width: 369px) {
                        padding-bottom: 6.9375rem;
                        height: calc(100vh - 3.3125rem);
                    }
                    @media screen and (min-width: 370px) and (max-width: 379px) {
                        padding-bottom: 6.9375rem;
                        height: calc(100vh - 4.3125rem);
                    }
                    @media screen and (min-width: 380px) and (max-width: 414px) {
                        padding-bottom: 6.9375rem;
                        height: calc(100vh - 4.5rem);
                    }
                }
                div[data-test-selector="customerAddressFormFields"] {
                    > div > div:last-child {
                        padding-bottom: 30px;
                    }
                }
            }

            div[class^='ModalContent'], div[class*=' ModalContent']{
                position: relative;
                padding-bottom: 9.5rem;
                height: calc(100vh - 9.5rem);

                @media screen and (max-width: 767px) {
                    padding-bottom: 9.5rem;
                    height: calc(100vh - 9.5rem);
                }
                @media screen and (max-width: 480px) {
                    height: calc(100vh - 2.5rem);
                }
            }

            .new-address-form,
            .edit-address-form {
                padding: 0 1.5625rem;
            }
                
            .border-seperator {
                border-bottom: 0.0625rem solid color: ${themeColors.colorBorderSeperator};
                padding-top: 0;
                margin-top: 1.25rem;
                margin-bottom: 1.25rem;
                width: 100%;
                display: block;
            }
            > div {
                border: 0.0625rem solid rgba(7,15,44,0.08);
                border-radius: 0.0625rem;
                background-color: ${themeColors.colorWhite};
                max-width: 35.625rem;
                margin-right: 0;
                margin-left: auto;
                margin-top: 0;
                max-height: 100vh;

                @media (max-width: 767px) {
                    max-width: 100%;
                    margin-right: 0;
                    margin-left: 0;
                }

                #modalTitle {
                    border-bottom: none;
                    padding-bottom: 0;
                    display: none;

                    h4 {
                        color: ${themeColors.colorPrimary};
                        font-family: 'Roboto', sans-serif;
                        font-size: ${themeFontSizes.size18};
                        font-weight: ${themeFontWeights.fontWeightMedium};
                        letter-spacing: 0;
                        line-height: 1.9375rem;
                        padding-top: 2.8125rem;
                        position: relative;

                        &:after {
                            height: 0.125rem;
                            width: 2.75rem;
                            background-color:  ${themeColors.colorRed};
                            content: '';
                            display: inline-block;
                            position: absolute;
                            bottom: -0.1875rem;
                            left: 0.0625rem;
                        }
                    }
                }

                .modal-title {
                    border-bottom: none;
                    padding-bottom: 0;

                    h2 {
                        display: none;

                        &.current {
                            display: block;
                            color: ${themeColors.colorPrimary};
                            font-family: 'Roboto', sans-serif;
                            font-size: ${themeFontSizes.size18};
                            font-weight: ${themeFontWeights.fontWeightMedium};
                            letter-spacing: 0;
                            line-height: 1.9375rem;
                            padding-top: 2.8125rem;
                            position: relative;
                            margin-bottom: 2.1875rem;
    
                            &:after {
                                height: 0.125rem;
                                width: 2.75rem;
                                background-color:  ${themeColors.colorRed};
                                content: "";
                                display: inline-block;
                                position: absolute;
                                bottom: -0.1875rem;
                                left: 0.0625rem;
                            }
                        }
                        
                    }

                    button[aria-labelledby="close-modal"] {
                        display: none;
                    }
                }
            }
            .toggle-content {
                display: none;

                &.active {
                    display: block;
                }
            }

            &.edit-billing-modal {
                div[class^='ModalContent'], div[class*=' ModalContent']{
                    z-index: 9;
                }
                .btn-wrapper {
                    z-index: 9;
                }
                
            }
        `,
    },
    addressBookModalCancelButton: { 
        css: css`
            color: color: ${themeColors.colorGreyCancel};
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size15};
            letter-spacing: 0;
            line-height: 1.3125rem;
            text-align: center;
            text-decoration: underline;
            padding: 0;
            background: none;
            border: none;

            &:hover,
            &:active, 
            &:focus {
                background: none !important;
                border: none !important;
                color: ${themeColors.colorPrimary};
            }

            &.cancel-btn {
                position: fixed;
                bottom: 1.8rem;
                left: auto;
                right: 25.625rem;
                @media screen and (max-width:389px) {
                  right: 17.625rem;
                }
                @media screen and (min-width:390px) and (max-width:480px) {
                    right: 18.625rem;
                }
            }
        `,
    },
    addressDisplayAndFormGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
};

const BillingAddress = ({
    address,
    onChange,
    countries,
    addressFieldDisplayCollection,
    extendedStyles,
    currentUserIsGuest,
    toaster,
    isBillingAddressUpdateRequired,
    cart,
    currentShipTosDataView,
    loadCurrentShipTos
}: Props) => {
    const [isCustomerSelectorModalOpen, setIsCustomerSelectorModalOpen] = useState(false);
    const [isEditAddressModalOpen, setIsEditAddressModalOpen] = useState(false);
    const [addressBeingEdited, setAddressBeingEdited] = useState<BillToModel | undefined>(undefined);
    const [billToAddress, setBillToAddress] = useState<ShipToModel | undefined>(undefined);
    useEffect(() => {
        if (!currentShipTosDataView.value && !currentShipTosDataView.isLoading) {
            loadCurrentShipTos();
        }
    });
    useEffect(() => {
        if (addressBeingEdited) {
            setIsEditAddressModalOpen(true);
        }
    }, [addressBeingEdited]);
   
    // useEffect(()=>{
    //     if (!currentUserIsGuest && address.firstName === "" && isBillingAddressUpdateRequired===false) {
    //         const reloadCount = sessionStorage.getItem('reloadCount')||"1";
    //         if (Number(reloadCount) < 2) {
    //             sessionStorage.setItem('reloadCount', String(reloadCount + 1));
    //             window.location.reload();
    //         } else {
    //             sessionStorage.removeItem('reloadCount');
    //         }
    //     }
    // },[address,isBillingAddressUpdateRequired]);

    useEffect(() => {
        if (currentShipTosDataView.value) {
            const billToAsShipTo = currentShipTosDataView.value.find(o => o.id === address.id);
            if (billToAsShipTo) {
                setBillToAddress(billToAsShipTo);
            }
        }
    }, [currentShipTosDataView]);

    const styles = mergeToNew(billingAddressStyles, extendedStyles);

    const selectAddressClickHandler = () => setIsCustomerSelectorModalOpen(true);

    const customerSelectorModalCloseHandler = () => setIsCustomerSelectorModalOpen(false);

    const selectCustomerHandler = (billTo: BillToModel) => {
        customerSelectorModalCloseHandler();
        onChange(billTo);
    };

    const editCustomerHandler = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, customer: BillToModel) => {
        event.preventDefault();
        setAddressBeingEdited(customer);
    };

    const editAddressModalCloseHandler = () => {
        setIsEditAddressModalOpen(false);
        setAddressBeingEdited(undefined);
    };

    const editAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: BillToModel) => {
        onChange(address);
        editAddressModalCloseHandler();
        customerSelectorModalCloseHandler();
        toaster.addToast({
            messageType: "success",
            body: "Address updated.",
        });
    };

    return (
        <>
            <GridContainer {...styles.container} data-test-selector="checkoutShipping_billingAddress">
                <GridItem {...styles.headingGridItem}>
                    <Typography {...styles.headingText} as="h3"  className={`${currentUserIsGuest ? 'mb-present' : ''}`}>
                        {translate("Billing Address")}
                    </Typography>
                    {!currentUserIsGuest &&
                        cart?.status !== "QuoteProposed" &&
                        !(cart?.status === "Cart" && cart?.type === "Job") && (
                            <>
                              {billToAddress?.properties?.isErpNumberNotAvailable === "true" && (
                                     <Link
                                     {...styles.selectSavedAddressLink}
                                     type="button"
                                     onClick={selectAddressClickHandler}
                                     data-test-selector="checkoutShipping_selectSavedAddress"
                                 >   
                                     <Icon src={EditIcon} />
                                     <VisuallyHidden>{translate("Select Saved Address")}</VisuallyHidden>
                                     <VisuallyHidden>{translate("For Billing Address")}</VisuallyHidden>
                                 </Link>
                              )}
                            </>                         
                        )}
                </GridItem>
                <GridItem {...styles.addressDisplayAndFormGridItem}>
                    {currentUserIsGuest || isBillingAddressUpdateRequired ? (
                        <>
                            {addressFieldDisplayCollection && countries && (
                                <BillingAddressForm
                                    countries={countries}
                                    fieldDisplay={addressFieldDisplayCollection}
                                    extendedStyles={styles.addressForm}
                                />
                            )}
                        </>
                    ) : (
                        <AddressInfoDisplay
                            {...address}
                            state={address.state?.abbreviation}
                            country={address.country?.abbreviation}
                            extendedStyles={styles.addressDisplay}
                        />
                    )}
                </GridItem>
            </GridContainer>
            {!addressBeingEdited && (
                <Modal
                    {...styles.addressBookModal}
                    {...styles.customAddressBookModal}
                    headline={translate("Address Book")}
                    isOpen={isCustomerSelectorModalOpen}
                    handleClose={customerSelectorModalCloseHandler}
                    data-test-selector="checkoutShipping_addressModal"
                >   
                    <Typography as="div" onClick={()=>customerSelectorModalCloseHandler()} {...styles.closeContainer}>
                        <span className="close-label">Close</span>
                        <Icon className="icon-close" src="XCircle"/>
                    </Typography>

                    <Typography as="div" className="modal-title">
                        <Typography as="h2" className="current">
                            {translate("Select Billing Address")}
                        </Typography>
                    </Typography>

                    <BillToSelector
                        onSelect={selectCustomerHandler}
                        onEdit={editCustomerHandler}
                        extendedStyles={styles.billToSelector}
                    />
                    <StyledWrapper {...styles.addressBookModalButtonsWrapper}>
                        <Button
                            {...styles.addressBookModalCancelButton}
                            onClick={customerSelectorModalCloseHandler}
                            data-test-selector="addressModal_cancel"
                            className="cancel-btn"
                        >
                            {translate("Cancel")}
                        </Button>
                    </StyledWrapper>
                </Modal>
            )}
            {addressBeingEdited && (
                <Modal
                    {...styles.addressBookModal}
                    {...styles.customAddressBookModal}
                    headline={translate("Edit Address")}
                    isOpen={isEditAddressModalOpen}
                    handleClose={editAddressModalCloseHandler}
                    data-test-selector="checkoutShipping_editAddressModal"
                    className="edit-billing-modal"
                >   
                    <Typography as="div" onClick={()=>editAddressModalCloseHandler()} {...styles.closeContainer}>
                        <span className="close-label">Close</span>
                        <Icon className="icon-close" src="XCircle"/>
                    </Typography>

                    <Typography as="div" className="modal-title">
                        <Typography as="h2" className="current">
                            {translate("Edit Billing Address")}
                        </Typography>
                    </Typography>
                    <CustomerAddressForm
                        address={addressBeingEdited}
                        countries={countries}
                        addressFieldDisplayCollection={addressFieldDisplayCollection}
                        onSubmit={editAddressFormSubmitHandler}
                        saveButtonTextOverride={translate("Save & Apply")}
                        onCancel={editAddressModalCloseHandler}
                        extendedStyles={styles.editAddressForm}
                    />
                </Modal>
            )}
        </>
    );
};

export default connect(mapStateToProps,mapDispatchToProps)(withToaster(BillingAddress));
